/**
 * Phase B1 — Dashboard banner. Lists count of active rentals released in
 * the last 30 days that have no active/paused billing schedule.
 * Dismissible per-session via sessionStorage. Super_admin gets a "View
 * all (incl. legacy)" link to see the historical backlog without the
 * date window.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, X } from "lucide-react";
import api from "@/lib/api";

const DISMISS_KEY = "rp_b1_unscheduled_banner_dismissed_at";
const DEFAULT_WINDOW_DAYS = 30;

function isDismissedThisSession() {
    if (typeof window === "undefined") return false;
    try {
        const v = window.sessionStorage.getItem(DISMISS_KEY);
        return Boolean(v);
    } catch {
        return false;
    }
}

export function UnscheduledRentalsBanner({ isSuperAdmin = false }) {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(isDismissedThisSession());

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const { data: res } = await api.get(
                    `/admin/billing/unscheduled-count?window_days=${DEFAULT_WINDOW_DAYS}`,
                );
                if (!cancelled) setData(res);
            } catch {
                /* silent — banner just hides */
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    if (dismissed) return null;
    if (!data || !data.count || data.count <= 0) return null;

    const onDismiss = () => {
        try { window.sessionStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setDismissed(true);
    };

    const sample = data.sample || [];
    const windowDays = data.window_days || DEFAULT_WINDOW_DAYS;
    // Phase B1 follow-up #4 — admin can now set up per-rental schedules,
    // so both roles get the same primary CTA. Only super_admin retains
    // the "View all (incl. legacy)" link because the backfill page is
    // still super_admin-only.
    const ctaCopy = "Review and set up billing";
    const ctaHref = "/admin/rentals?has_schedule=false";

    return (
        <div
            data-testid="b1-unscheduled-banner"
            className="mt-4 rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3 flex items-start gap-3"
        >
            <span
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200"
                aria-hidden="true"
            >
                <AlertTriangle size={16} />
            </span>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-950">
                    <span data-testid="b1-unscheduled-count">{data.count}</span>
                    {" "}
                    rental{data.count === 1 ? "" : "s"} released in the last {windowDays} days {data.count === 1 ? "is" : "are"} not on a billing schedule.
                </p>
                <p className="text-xs text-amber-900/80 mt-1">
                    These rentals will not auto-invoice. Set up a recurring billing schedule to start monthly invoicing.
                </p>
                {sample.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="b1-unscheduled-sample">
                        {sample.slice(0, 3).map((r) => (
                            <li key={r.rental_id}>
                                <Link
                                    to={`/admin/rentals/${r.rental_id}`}
                                    data-testid={`b1-unscheduled-sample-${r.rental_id}`}
                                    className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-0.5 text-[11px] font-medium text-amber-900 ring-1 ring-inset ring-amber-200 hover:bg-amber-100/60 transition-colors"
                                >
                                    {r.company_name || "Customer"} · #{r.unit_number || "—"}
                                </Link>
                            </li>
                        ))}
                        {data.count > 3 && (
                            <li className="text-[11px] text-amber-900/70 self-center">
                                +{data.count - 3} more
                            </li>
                        )}
                    </ul>
                )}
                {isSuperAdmin && (
                    <p className="mt-2 text-[11px] text-amber-900/75">
                        <Link
                            to="/admin/billing/backfill"
                            data-testid="b1-unscheduled-view-all"
                            className="underline underline-offset-2 hover:text-amber-950"
                        >
                            View all unscheduled (incl. legacy)
                        </Link>
                    </p>
                )}
            </div>
            <div className="flex items-start gap-2 shrink-0">
                <Link
                    to={ctaHref}
                    data-testid="b1-unscheduled-cta"
                    className="inline-flex items-center gap-1 rounded-md bg-amber-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-800 transition-colors"
                >
                    {ctaCopy}
                    <ArrowUpRight size={12} aria-hidden="true" />
                </Link>
                <button
                    type="button"
                    onClick={onDismiss}
                    data-testid="b1-unscheduled-dismiss"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-amber-700 hover:bg-amber-100 transition-colors"
                    aria-label="Dismiss"
                >
                    <X size={14} aria-hidden="true" />
                </button>
            </div>
        </div>
    );
}

export default UnscheduledRentalsBanner;
